import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { SavedLesson } from '@/mocks/lessons';
import FormattedMessage from '@/pages/ai-assistant/components/FormattedMessage';

interface HomeworkHelpProps {
  lesson: SavedLesson;
}

export default function HomeworkHelp({ lesson }: HomeworkHelpProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [expanded, setExpanded] = useState<string | null>(null);

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-white/30' : 'text-black/30',
    textFaint: isDark ? 'text-white/25' : 'text-black/25',
    iconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    iconMuted: isDark ? 'text-white/50' : 'text-black/40',
    cardBg: isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]',
    cardBorder: isDark ? 'border-white/[0.05]' : 'border-black/[0.05]',
    cardBorderHover: isDark ? 'hover:border-white/[0.10]' : 'hover:border-black/[0.10]',
    qaIconBg: isDark ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-black/[0.04] border-black/[0.06]',
    qaIcon: isDark ? 'text-white/30' : 'text-black/30',
    starFill: isDark ? 'text-amber-400/60' : 'text-amber-500/60',
    starEmpty: isDark ? 'text-amber-400/20' : 'text-amber-500/20',
    cardDivider: isDark ? 'border-white/[0.04]' : 'border-black/[0.04]',
    aiIconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    aiIcon: isDark ? 'text-white/30' : 'text-black/30',
    btnGhost: isDark ? 'bg-white/[0.04] text-white/50 border-white/[0.06]' : 'bg-black/[0.04] text-black/50 border-black/[0.06]',
    ctaBorder: isDark ? 'border-white/[0.05] border-dashed text-white/30 hover:bg-white/[0.04]' : 'border-black/[0.05] border-dashed text-black/30 hover:bg-black/[0.04]',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pb-4"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-lightbulb-line ${t.iconMuted} text-sm`} />
          </div>
        </div>
        <div>
          <h3 className={`${t.textPrimary} text-sm font-semibold`}>Homework Help</h3>
          <p className={`${t.textFaint} text-[11px]`}>AI-answered questions</p>
        </div>
      </div>

      <div className="space-y-2.5">
        {lesson.homeworkHelp.map((item, i) => {
          const isExpanded = expanded === item.id;

          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.3 }}
            >
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => setExpanded(isExpanded ? null : item.id)}
                className={`w-full text-left p-4 rounded-2xl ${t.cardBg} border ${t.cardBorder} ${t.cardBorderHover} transition-all duration-200 cursor-pointer`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-xl ${t.qaIconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-question-mark ${t.qaIcon} text-xs`} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`${t.textPrimary} text-sm font-medium leading-snug`}>{item.question}</h4>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((r) => (
                          <div key={r} className="w-3 h-3 flex items-center justify-center">
                            {r <= item.userRating ? (
                              <i className={`ri-star-fill ${t.starFill} text-[10px]`} />
                            ) : (
                              <i className={`ri-star-line ${t.starEmpty} text-[10px]`} />
                            )}
                          </div>
                        ))}
                      </div>
                      <span className={`${isDark ? 'text-white/20' : 'text-black/20'} text-[10px]`}>{item.userRating}/5</span>
                    </div>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                    {isExpanded ? (
                      <i className={`ri-arrow-up-s-line ${t.textFaint} text-sm`} />
                    ) : (
                      <i className={`ri-arrow-down-s-line ${t.textFaint} text-sm`} />
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className={`mt-3 pt-3 border-t ${t.cardDivider}`}
                    >
                      <div className="flex items-start gap-2 mb-3">
                        <div className={`w-6 h-6 rounded-lg ${t.aiIconBg} flex items-center justify-center flex-shrink-0`}>
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className={`ri-sparkling-line ${t.aiIcon} text-[10px]`} />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <FormattedMessage content={item.aiAnswer} isUser={false} theme={resolvedTheme} />
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          className={`flex-1 py-2 rounded-xl text-[11px] font-semibold border ${t.btnGhost} cursor-pointer flex items-center justify-center gap-1.5`}
                        >
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-thumb-up-line text-[10px]" />
                          </div>
                          Helpful
                        </motion.button>
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          className={`flex-1 py-2 rounded-xl text-[11px] font-semibold border ${t.btnGhost} cursor-pointer flex items-center justify-center gap-1.5`}
                        >
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-chat-1-line text-[10px]" />
                          </div>
                          Follow Up
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </motion.div>
          );
        })}
      </div>

      {/* Ask a question CTA */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        className={`w-full mt-3 py-3 rounded-2xl ${t.ctaBorder} text-xs font-medium flex items-center justify-center gap-2 cursor-pointer transition-colors`}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-add-line text-sm" />
        </div>
        Ask LAVYN a question about this lesson
      </motion.button>
    </motion.div>
  );
}